import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, InputBase, Stack } from "@mui/material";

import { DecimalInput } from "../../../../components/DecimalInput";
import Switch from "../../common/Switch";
import TextField from "../../../../components/TextField";
import Tooltip from "../../../../components/Tooltip";
import { ReactComponent as CaretIcon } from "../../../../assets/caret.svg";

import { selectProfitCalculator } from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { updateLookupThunk } from "../../../../features/productAnalyzer/productAnalyzer.thunk";
import { useAppDispatch } from "../../../../store/hooks";
import Calculator from "../../../../helpers/profitCalculator";
import {
  formatCurrency,
  formatPercent,
  toNumber,
  toPrice,
} from "../../../../helpers/formatter";
import {
  INBOUND_PLACEMENT_TOOLTIP,
  INBOUND_PLACEMENT_TYPES,
} from "../../../../constants";

import {
  Label,
  Panel,
  PanelLabel,
  PanelRow,
  useStyles,
  Row,
} from "./index.styles";

interface ValueProps {
  value: string;
  singleColored?: boolean;
  crossLined?: boolean;
  editable?: boolean;
  editProps?: object;
  isMetCriteria?: boolean;
  onClick?: () => void;
  onChange?: (val: number) => void;
}

export const Value: React.FunctionComponent<ValueProps> = ({
  value,
  singleColored = false,
  crossLined = false,
  editable = false,
  editProps = { input: { step: 0.01, min: 0 } },
  isMetCriteria,
  onClick,
  onChange,
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<number>(
    parseFloat(value.replace(/(%)|(\$)/, "")),
  );

  useEffect(() => {
    setInputValue(parseFloat(value.replace(/(%)|(\$)/, "")));
  }, [value]);

  const handleClick = () => {
    onClick?.();
    if (editable) {
      setIsEditing(true);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      {!isEditing && (
        <Box
          sx={{
            fontSize: "12px",
            lineHeight: "18px",
            fontWeight: 600,
            color: singleColored
              ? "#181D27"
              : isMetCriteria
                ? "#047647"
                : "#B42419",
            textAlign: "right",
            textDecoration: crossLined ? "line-through" : "none",
            cursor: editable ? "pointer" : onClick ? "copy" : "auto",
            textDecorationLine: editable ? "underline" : "none",
            textDecorationStyle: "dotted",
            textDecorationColor: "#202020",
            textUnderlineOffset: "2px",
          }}
          onClick={handleClick}
        >
          {value}
        </Box>
      )}
      {isEditing && (
        <InputBase
          type="number"
          value={isFocused ? inputValue : inputValue.toFixed(2)}
          onChange={(event) => {
            onChange?.(toPrice(event.target.value));
            setInputValue(toPrice(event.target.value));
          }}
          onBlur={(event) => {
            // onChange?.(toPrice(event.target.value));
            setIsFocused(false);
            setIsEditing(false);
          }}
          slotProps={editProps}
          className={classes.inputBase}
          autoFocus
          onFocus={(event) => {
            setIsFocused(true);
            event.target.select();
          }}
        />
      )}
    </Box>
  );
};

const ProfitCalculator = () => {
  const dispatch = useAppDispatch();
  const { profitCalculator, feeRules } = useSelector(selectProfitCalculator);
  const classes = useStyles();

  const [showProfit, setShowProfit] = useState<boolean>(false);
  const [showMaxCost, setShowMaxCost] = useState<boolean>(false);
  const [showTotalFees, setShowTotalFees] = useState<boolean>(false);
  const [showAmzPayout, setShowAmzPayout] = useState<boolean>(false);
  const [isMiscPercentEditable, setIsMiscPercentEditable] =
    useState<boolean>(false);

  const handleCostChange = (costPrice: number) => {
    dispatch(updateLookupThunk({ costPrice }));
  };

  const handlePriceChange = (salePrice: number) => {
    dispatch(updateLookupThunk({ salePrice }));
  };

  const {
    profit,
    totalFee,
    margin,
    roi,
    maxCost,
    referralFee,
    referralHint,
    fbaFee,
    storageFee,
    prepFee,
    inboundPlacementFee,
    miscFeeAmount,
    miscFeePercentAmount,
    breakevenSalePrice,
    payout,
    packageCost,
    discount,
  } = new Calculator(profitCalculator, feeRules).calculate();

  return (
    <Stack>
      <Row>
        <Label>Sale Price</Label>
        <Box>
          <DecimalInput
            prefix="$"
            value={profitCalculator.salePrice}
            onEditing={handlePriceChange}
            size="md"
            align="right"
          />
        </Box>
      </Row>

      <Row>
        <Label>Cost Price</Label>
        <Box>
          <DecimalInput
            prefix="$"
            value={profitCalculator.costPrice}
            onEditing={handleCostChange}
            size="md"
            align="right"
          />
        </Box>
      </Row>

      {discount > 0 && (
        <Row>
          <Label>Cost Price After Discount</Label>
          <DecimalInput
            prefix="$"
            value={
              profitCalculator.costPrice -
              (profitCalculator.costPrice * profitCalculator.totalDiscount) /
                100
            }
            size="md"
            align="right"
            disabled
          />
          {/* <Value
            value={formatCurrency(
              profitCalculator.costPrice -
                (profitCalculator.costPrice * discountPercent) / 100
            )}
            singleColored
          /> */}
        </Row>
        // <Panel>
        //   <PanelRow>
        //     <PanelLabel noBorder>Cost Price After Discount</PanelLabel>
        //     <Value
        //       value={formatCurrency(
        //         profitCalculator.costPrice -
        //           (profitCalculator.costPrice * discountPercent) / 100
        //       )}
        //       singleColored
        //     />
        //   </PanelRow>
        // </Panel>
      )}

      <Row>
        <Label>Fulfilment Type</Label>
        <Box>
          <Switch
            onLabel="FBM"
            offLabel="FBA"
            checked={profitCalculator.fulfilmentType === "FBM"}
            fbmColor="#EBF6FF"
            fbaColor="#DCFCE7"
            onChange={(val) => {
              dispatch(
                updateLookupThunk({ fulfilmentType: val ? "FBM" : "FBA" }),
              );
            }}
          />
        </Box>
      </Row>

      {profitCalculator.fulfilmentType === "FBA" && (
        <Row>
          <Label>Storage (Months)</Label>
          <Box>
            <TextField
              type="number"
              onKeyDown={(e) => {
                if (e.key === "." || e.key === ",") {
                  e.preventDefault();
                }
              }}
              value={profitCalculator.storageTime}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(
                  updateLookupThunk({
                    storageTime: toNumber(event.target.value),
                  }),
                );
              }}
              slotProps={{ input: { min: 0 } }}
              textAlign="right"
              autoSelect
            />
          </Box>
        </Row>
      )}

      {profitCalculator.fulfilmentType === "FBM" && (
        <Row>
          <Label>FBM Cost</Label>
          <Box>
            <DecimalInput
              prefix="$"
              value={profitCalculator.fbmCost}
              onEditing={(fbmCost: number) => {
                dispatch(
                  updateLookupThunk({
                    fbmCost,
                  }),
                );
              }}
              size="md"
              align="right"
            />
          </Box>
        </Row>
      )}

      <Row isOpen={showProfit} noBorder>
        <Label
          sx={{ fontWeight: 700, gap: "6px" }}
          onClick={() => setShowProfit(!showProfit)}
          underLined
          noBorder
        >
          Profit <CaretIcon />
        </Label>
        <Value
          value={formatCurrency(profit)}
          isMetCriteria={profit >= toNumber(profitCalculator.minProfit)}
        />
      </Row>

      <Panel hidden={!showProfit}>
        <PanelRow>
          <PanelLabel noBorder>Sale Price</PanelLabel>
          <Value
            value={formatCurrency(profitCalculator.salePrice)}
            singleColored
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>
            Cost Price
            {profitCalculator.multipackQuantity > 1
              ? ` x ${profitCalculator.multipackQuantity}`
              : ""}
          </PanelLabel>
          <Value value={formatCurrency(-packageCost)} singleColored />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Total Fees</PanelLabel>
          <Value value={formatCurrency(-totalFee)} singleColored />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Discount</PanelLabel>
          <Value value={formatCurrency(discount)} singleColored />
        </PanelRow>
      </Panel>

      <Row>
        <Label sx={{ fontWeight: 700 }} noBorder>
          Profit Margin
        </Label>
        <Value
          value={formatPercent(margin)}
          isMetCriteria={profit >= toNumber(profitCalculator.minProfit)}
        />
      </Row>

      <Row>
        <Label sx={{ fontWeight: 700 }} noBorder>
          ROI
        </Label>
        <Value
          value={formatPercent(roi)}
          isMetCriteria={roi >= toNumber(profitCalculator.minRoi)}
        />
      </Row>

      <Row isOpen={showMaxCost} noBorder>
        <Label
          sx={{ fontWeight: 700, gap: "6px" }}
          onClick={() => setShowMaxCost(!showMaxCost)}
          underLined
          noBorder
        >
          Maximum Cost <CaretIcon />
        </Label>
        <Value
          value={formatCurrency(maxCost)}
          singleColored={profitCalculator.costPrice === 0}
          isMetCriteria={profitCalculator.costPrice <= maxCost}
          onClick={
            !isFinite(maxCost)
              ? undefined
              : () => dispatch(updateLookupThunk({ costPrice: maxCost }))
          }
        />
      </Row>

      <Panel hidden={!showMaxCost}>
        <PanelRow>
          <PanelLabel noBorder>Min. ROI</PanelLabel>
          <Value
            value={formatPercent(profitCalculator.minRoi)}
            singleColored
            editable
            onChange={(minRoi) => {
              dispatch(updateLookupThunk({ minRoi }));
            }}
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Min. Profit</PanelLabel>
          <Value
            value={formatCurrency(profitCalculator.minProfit)}
            singleColored
            editable
            onChange={(minProfit) => {
              dispatch(updateLookupThunk({ minProfit }));
            }}
          />
        </PanelRow>
      </Panel>

      <Row isOpen={showTotalFees} noBorder>
        <Label
          onClick={() => setShowTotalFees(!showTotalFees)}
          sx={{ gap: "6px" }}
          underLined
          noBorder
        >
          Total Fees <CaretIcon />
        </Label>
        <Value value={formatCurrency(totalFee)} singleColored />
      </Row>

      <Panel hidden={!showTotalFees}>
        <PanelRow>
          <Tooltip
            title={
              referralHint ? (
                <div dangerouslySetInnerHTML={{ __html: referralHint }}></div>
              ) : (
                ""
              )
            }
          >
            <PanelLabel noBorder underLined={!!referralHint}>
              Referral Fee
            </PanelLabel>
          </Tooltip>
          <Value value={formatCurrency(referralFee)} singleColored />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>
            Fulfilment ({profitCalculator.fulfilmentType})
          </PanelLabel>
          <Value
            value={formatCurrency(
              profitCalculator.fulfilmentType === "FBA"
                ? fbaFee
                : profitCalculator.fbmCost,
            )}
            singleColored
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Closing Fee</PanelLabel>
          <Value
            value={formatCurrency(profitCalculator.closingFee)}
            singleColored
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel
            crossLined={profitCalculator.fulfilmentType === "FBM"}
            noBorder
          >
            Storage Fee
          </PanelLabel>
          <Value
            value={formatCurrency(storageFee)}
            singleColored
            crossLined={profitCalculator.fulfilmentType === "FBM"}
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel
            crossLined={profitCalculator.fulfilmentType === "FBM"}
            noBorder
          >
            Prep Fee
          </PanelLabel>
          <Value
            value={formatCurrency(prepFee)}
            singleColored
            crossLined={profitCalculator.fulfilmentType === "FBM"}
            editable
            onChange={(prepFee) => {
              dispatch(updateLookupThunk({ prepFee }));
            }}
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel
            crossLined={profitCalculator.fulfilmentType === "FBM"}
            noBorder
          >
            Inbound Shipping
          </PanelLabel>
          <Value
            value={formatCurrency(profitCalculator.inboundShipping)}
            singleColored
            crossLined={profitCalculator.fulfilmentType === "FBM"}
            editable
            onChange={(inboundShipping) => {
              dispatch(updateLookupThunk({ inboundShipping }));
            }}
          />
        </PanelRow>
        <PanelRow>
          <Tooltip
            title={
              <Box
                dangerouslySetInnerHTML={{
                  __html:
                    INBOUND_PLACEMENT_TOOLTIP[
                      profitCalculator.inboundPlacementService
                    ],
                }}
              />
            }
          >
            <PanelLabel
              crossLined={profitCalculator.fulfilmentType === "FBM"}
              onClick={() => {
                const index = INBOUND_PLACEMENT_TYPES.indexOf(
                  profitCalculator.inboundPlacementService,
                );
                dispatch(
                  updateLookupThunk({
                    inboundPlacementService:
                      INBOUND_PLACEMENT_TYPES[
                        (index + 1) % INBOUND_PLACEMENT_TYPES.length
                      ],
                  }),
                );
              }}
              underLined
              noBorder
            >
              Inbound Placement (
              {profitCalculator.inboundPlacementService.slice(0, 1)})
            </PanelLabel>
          </Tooltip>
          <Value
            value={formatCurrency(inboundPlacementFee)}
            singleColored
            crossLined={profitCalculator.fulfilmentType === "FBM"}
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Misc Fee</PanelLabel>
          <Value
            value={formatCurrency(miscFeeAmount)}
            singleColored
            editable
            onChange={(miscFee) => {
              dispatch(updateLookupThunk({ miscFee }));
            }}
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>
            Misc Fee (
            {!isMiscPercentEditable && (
              <span
                className={classes.textUnderline}
                onClick={() => {
                  setIsMiscPercentEditable(true);
                }}
              >
                {toPrice(profitCalculator.miscFeePercent)}%
              </span>
            )}
            {isMiscPercentEditable && (
              <>
                <InputBase
                  type="number"
                  value={profitCalculator.miscFeePercent}
                  onChange={(event) => {
                    dispatch(
                      updateLookupThunk({
                        miscFeePercent: toPrice(event.target.value),
                      }),
                    );
                  }}
                  onBlur={() => {
                    setIsMiscPercentEditable(false);
                  }}
                  onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
                    event.target.select()
                  }
                  slotProps={{
                    input: { step: 0.01, min: 0, max: 100 },
                  }}
                  className={classes.inputBase}
                  autoFocus
                />
                %
              </>
            )}
            &nbsp;of cost)
          </PanelLabel>
          <Value value={formatCurrency(miscFeePercentAmount)} singleColored />
        </PanelRow>
      </Panel>

      <Row>
        <Label noBorder>Discount</Label>
        <Value
          value={formatCurrency(
            (profitCalculator.costPrice * profitCalculator.totalDiscount) / 100,
          )}
          singleColored
        />
      </Row>

      <Row>
        <Label noBorder>Breakeven Sale Price</Label>
        <Value
          value={formatCurrency(breakevenSalePrice)}
          singleColored
          onClick={() => {
            dispatch(updateLookupThunk({ salePrice: breakevenSalePrice }));
          }}
        />
      </Row>

      <Row isOpen={showAmzPayout} noBorder>
        <Label
          onClick={() => setShowAmzPayout(!showAmzPayout)}
          sx={{ gap: "6px" }}
          underLined
          noBorder
        >
          Estimated Amz.Payout <CaretIcon />
        </Label>
        <Value value={formatCurrency(payout)} singleColored />
      </Row>

      <Panel hidden={!showAmzPayout}>
        <PanelRow>
          <PanelLabel noBorder>Sale Price</PanelLabel>
          <Value
            value={formatCurrency(profitCalculator.salePrice)}
            singleColored
          />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Referral Fee</PanelLabel>
          <Value value={formatCurrency(-referralFee)} singleColored />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Fulfilment Fee</PanelLabel>
          <Value value={formatCurrency(-fbaFee)} singleColored />
        </PanelRow>
        <PanelRow>
          <PanelLabel noBorder>Closing Fee</PanelLabel>
          <Value
            value={formatCurrency(-profitCalculator.closingFee)}
            singleColored
          />
        </PanelRow>
      </Panel>

      <Row>
        <Label sx={{ fontWeight: 700 }}>Multi-pack Quantity</Label>
        <Box>
          <TextField
            type="number"
            value={profitCalculator.multipackQuantity}
            onKeyDown={(e) => {
                if (e.key === "." || e.key === ",") {
                  e.preventDefault();
                }
              }}
            onChange={(event) => {
              dispatch(
                updateLookupThunk({
                  multipackQuantity: Math.max(toNumber(event.target.value), 1),
                }),
              );
            }}
            slotProps={{ input: { min: 1 } }}
            textAlign="right"
            autoSelect
          />
        </Box>
      </Row>

      <Row>
        <Label sx={{ fontWeight: 700 }}>Quantity</Label>
        <Box>
          <TextField
            type="number"
            value={profitCalculator.quantity}
            onKeyDown={(e) => {
                if (e.key === "." || e.key === ",") {
                  e.preventDefault();
                }
              }}
            onChange={(event) => {
              dispatch(
                updateLookupThunk({
                  quantity: Math.max(toNumber(event.target.value), 1),
                }),
              );
            }}
            slotProps={{ input: { min: 1 } }}
            textAlign="right"
            autoSelect
          />
        </Box>
      </Row>

      {profitCalculator.quantity !== 1 && (
        <Panel>
          <PanelRow>
            <PanelLabel noBorder>Cost</PanelLabel>
            <Value
              value={formatCurrency(
                profitCalculator.costPrice * profitCalculator.quantity,
              )}
              singleColored
            />
          </PanelRow>
          <PanelRow>
            <PanelLabel noBorder>Sale</PanelLabel>
            <Value
              value={formatCurrency(
                profitCalculator.salePrice * profitCalculator.quantity,
              )}
              singleColored
            />
          </PanelRow>
          <PanelRow>
            <PanelLabel noBorder>Total Profit</PanelLabel>
            <Value
              value={formatCurrency(profit * profitCalculator.quantity)}
              singleColored
            />
          </PanelRow>
        </Panel>
      )}
    </Stack>
  );
};

export default memo(ProfitCalculator);
